import { useState, useMemo, FC, RefObject } from 'react';
import { Typography } from '@kizen/kds/Typography';
import { useTranslation } from 'react-i18next';
import { MarkdownContainer } from './styles';
import { InteractiveIconButton } from '../InteractiveIconButton';
import { markdownToHtmlSync } from '@kizen/parse-md';
import { StatusDetailsDropdown } from './StatusDetailsDropdown';
import { TypingIndicator } from './TypingIndicator';
import KizenAILogo from 'pages/Marketplace/assets/kizen-ai.svg?react';

interface MessageProps {
  id: string;
  isUserMessage: boolean;
  messageContent: string;
  isResponseLoading?: boolean;
  isResponseRefining?: boolean;
  isLastAssistantRole: boolean;
  isErrorMessage?: boolean;
  header?: string;
  avatarSrc?: string;
  avatarAlt?: string;
  isAnimating?: boolean;
  messageRef?: RefObject<HTMLDivElement>;
  streaming?: boolean;
  onAnimationEnd?: (id: string) => void;
  isLastMessage?: boolean;
  awaitingFinal?: boolean;
  buildCompleted?: boolean;
  bottomRef?: RefObject<HTMLDivElement>;
  containerHeight: number;
  showFixedHeight?: boolean;
  showFeedbackIcons?: boolean;
  hideRefresh?: boolean;
  renderMarkdown?: boolean;
  disableMessageAnimation?: boolean;
  bubble?: boolean;
  showAvatar?: boolean;
  floatAbove?: boolean;
  onCopyMessage?: (messageContent: string) => void;
  onLikeMessage?: () => void;
  onDislikeMessage?: () => void;
  onRefreshMessage?: () => void;
  statusDetails?: string;
}

export const Message: FC<MessageProps> = ({
  id,
  isUserMessage,
  messageContent,
  isResponseLoading = false,
  isResponseRefining = false,
  showFeedbackIcons = true,
  hideRefresh = false,
  buildCompleted = false,
  isErrorMessage = false,
  header,
  avatarSrc,
  avatarAlt = 'AI Avatar',
  messageRef,
  isAnimating = false,
  streaming,
  onAnimationEnd,
  isLastMessage,
  isLastAssistantRole,
  bottomRef,
  containerHeight,
  awaitingFinal,
  renderMarkdown = false,
  disableMessageAnimation = false,
  bubble = true,
  showAvatar = true,
  floatAbove = false,
  onCopyMessage,
  onLikeMessage,
  onDislikeMessage,
  onRefreshMessage,
  statusDetails,
}) => {
  const { t } = useTranslation();
  const [isLikedClicked, setIsLikedClicked] = useState(false);
  const [isDislikedClicked, setIsDislikedClicked] = useState(false);
  const hasHeader = !isUserMessage && header;

  const getMessageStyles = () => {
    if (isResponseLoading || isResponseRefining) {
      return {
        minHeight: '25px',
      };
    }
    return {};
  };

  const shouldAnimate = isAnimating && !streaming && !disableMessageAnimation;

  const bubbleClassName = useMemo(() => {
    if (!bubble) {
      return 'text-font-placeholder ml-[25px]';
    }
    const baseClasses =
      'whitespace-pre-wrap rounded-tr-[10px] rounded-tl-[10px] pr-[12px] pl-[12px] pt-[10px] pb-[10px] max-w-[min(800px,92%)] break-words overflow-hidden';
    const colorClasses = isUserMessage
      ? 'bg-ai-user-bg text-ai-user-text rounded-bl-[10px]'
      : 'bg-ai-system-bg text-ai-system-text rounded-br-[10px]';
    return `${colorClasses} ${baseClasses}`;
  }, [bubble, isUserMessage]);

  const showCopyButton = useMemo(() => {
    return onCopyMessage && !isResponseLoading && !isResponseRefining;
  }, [onCopyMessage, isResponseLoading, isResponseRefining]);

  const showLikeButton = useMemo(() => {
    return (
      onLikeMessage &&
      isDislikedClicked === false &&
      !isResponseLoading &&
      !isResponseRefining &&
      !isErrorMessage
    );
  }, [
    onLikeMessage,
    isDislikedClicked,
    isResponseLoading,
    isResponseRefining,
    isErrorMessage,
  ]);

  const showDislikeButton = useMemo(() => {
    return (
      onDislikeMessage &&
      isLikedClicked === false &&
      !isResponseLoading &&
      !isResponseRefining &&
      !isErrorMessage
    );
  }, [
    onDislikeMessage,
    isLikedClicked,
    isResponseLoading,
    isResponseRefining,
    isErrorMessage,
  ]);

  const showRefreshButton = useMemo(() => {
    if (!onRefreshMessage) return false;
    const invalidState = hideRefresh || awaitingFinal || buildCompleted;
    return (
      (!isResponseLoading && !isResponseRefining && !invalidState) ||
      (isErrorMessage && invalidState)
    );
  }, [
    onRefreshMessage,
    isResponseLoading,
    isResponseRefining,
    awaitingFinal,
    hideRefresh,
    buildCompleted,
    isErrorMessage,
  ]);

  const parsedMarkDown = useMemo(
    () => markdownToHtmlSync(messageContent, { forceExternalLinks: true }),
    [messageContent]
  );

  const heightCalulationContainer = useMemo(() => {
    if (!isUserMessage && isLastMessage && !awaitingFinal && !buildCompleted) {
      return {
        minHeight: containerHeight,
      };
    } else if (
      awaitingFinal ||
      buildCompleted ||
      isErrorMessage ||
      ((isResponseLoading || isResponseRefining) &&
        messageContent &&
        isLastMessage)
    ) {
      // handle loading and completed build
      return { minHeight: containerHeight / 4 };
    }
    return {};
  }, [
    isLastMessage,
    isUserMessage,
    containerHeight,
    awaitingFinal,
    buildCompleted,
    isErrorMessage,
    isResponseLoading,
    isResponseRefining,
    messageContent,
  ]);

  return (
    <div
      style={heightCalulationContainer}
      className={`flex flex-col items-start ${floatAbove ? 'absolute top-0 left-0 right-0 z-10' : ''} ${!bubble && !floatAbove ? 'mt-[-18px]' : ''}`}
    >
      <div
        id={id}
        ref={messageRef}
        className={`flex w-full ${isUserMessage ? 'justify-end' : 'justify-start'} ${shouldAnimate ? 'animate-fadeUp' : ''}`}
        style={getMessageStyles()}
        onAnimationEnd={() => onAnimationEnd?.(id)}
      >
        <div
          className={`flex items-end w-full gap-[5px] ${isUserMessage ? 'justify-end' : 'justify-start'}`}
        >
          {!isUserMessage && showAvatar && (
            <div className="flex w-[20px] h-[20px] flex-shrink-0">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={avatarAlt}
                  className="w-full h-full object-contain"
                />
              ) : (
                <KizenAILogo className="w-full h-full" />
              )}
            </div>
          )}
          <div className={bubbleClassName}>
            {hasHeader && (
              <div className="w-full pb-[20px] pt-[5px]">
                <div className="flex justify-between items-center w-full gap-[10px] text-font-primary">
                  <Typography
                    enableTooltip={false}
                    weight="bold"
                    color="ai-system-text"
                  >
                    {header}
                  </Typography>
                </div>
              </div>
            )}

            <div className={hasHeader ? 'pb-[15px]' : ''}>
              {!bubble && statusDetails ? (
                <StatusDetailsDropdown
                  content={statusDetails}
                  isStreaming={isResponseRefining ?? false}
                  summaryContent={messageContent}
                />
              ) : isResponseLoading || isResponseRefining ? (
                <div className={!bubble ? 'pt-[18px]' : ''}>
                  <TypingIndicator
                    key="loader"
                    color={
                      !bubble
                        ? 'background-dark'
                        : isUserMessage
                          ? 'ai-user-text'
                          : 'ai-system-text'
                    }
                    dotSize={messageContent ? '2px' : '5px'}
                    label={messageContent}
                  />
                </div>
              ) : !bubble ? (
                <div className="mt-[10px] mb-[6px]">
                  <Typography size="md" color="font-placeholder">
                    {messageContent}
                  </Typography>
                </div>
              ) : renderMarkdown ? (
                <MarkdownContainer
                  className="kds"
                  color={isUserMessage ? 'ai-user-text' : 'ai-system-text'}
                  dangerouslySetInnerHTML={{
                    __html: parsedMarkDown,
                  }}
                />
              ) : (
                <Typography color="ai-system-text">{messageContent}</Typography>
              )}
            </div>
          </div>
        </div>
      </div>
      {!isUserMessage && bubble && showFeedbackIcons && (
        <div className="p-2 mt-2 ml-[30px]">
          <div className="flex gap-[10px]">
            {showCopyButton && (
              <InteractiveIconButton
                icon="action-copy"
                tooltip={t('Copy Message')}
                tooltipPosition="bottom"
                onActive={() => onCopyMessage?.(messageContent)}
                isActive={false}
                persistActiveState={false}
              />
            )}
            {isLastAssistantRole && showLikeButton && (
              <InteractiveIconButton
                icon="action-like"
                tooltip={t('Good Response')}
                tooltipPosition="bottom"
                onActive={() => {
                  setIsLikedClicked((prev) => {
                    if (prev === true) {
                      return false;
                    } else {
                      onLikeMessage?.();
                      return true;
                    }
                  });
                }}
                isActive={isLikedClicked}
              />
            )}
            {isLastAssistantRole && showDislikeButton && (
              <InteractiveIconButton
                icon="action-dislike"
                tooltip={t('Bad Response')}
                tooltipPosition="bottom"
                onActive={() => {
                  setIsDislikedClicked((prev) => {
                    if (prev === true) {
                      return false;
                    } else {
                      onDislikeMessage?.();
                      return true;
                    }
                  });
                }}
                isActive={isDislikedClicked}
              />
            )}
            {isLastAssistantRole && showRefreshButton && (
              <InteractiveIconButton
                icon="action-refresh"
                tooltip={t('Regenerate')}
                tooltipPosition="bottom"
                onActive={() => onRefreshMessage?.()}
              />
            )}
          </div>
        </div>
      )}
      {isLastMessage && <div ref={bottomRef} className="h-px w-full" />}
    </div>
  );
};
